/**
 * Alert Actions vocabulary shared by the site page (local node, "Alert Actions") and the
 * cloud page ("Central Alert Actions"), and by both Alert Rules forms.
 *
 * Per rule (command-center alert_configs):
 *   Dashboard    websocket_enabled        — live alert on the boards (default ON)
 *   Email        email_enabled + email_recipients
 *   Message API  message_api_enabled + message_api_recipients
 *   Sent by      notify_via 'local' (this site's channels) | 'cloud' (Central Alert Actions)
 *
 * Channels (where email / messages go out from): 'email' = SMTP server,
 * 'message_api' = Message API Server. One set per site, one set on the cloud.
 */

export type AlertChannelKey = 'email' | 'message_api';

export interface AlertChannelState {
  channel: AlertChannelKey;
  enabled: boolean;
  configured: boolean;
  settings: Record<string, any>;
  has_secret: boolean;
  last_test_at: string | null;
  last_test_ok: boolean | null;
  last_test_message: string | null;
  updated_at: string | null;
  /** Email channel only: the Gmail API sender's connection. */
  gmail?: { has_client_secret: boolean; connected: boolean; account: string | null };
}

export interface SaveChannelBody {
  enabled?: boolean;
  settings?: Record<string, any>;
  /** Non-empty replaces the stored secret; empty/undefined keeps it. */
  secret?: string;
  clear_secret?: boolean;
  /** Gmail API sender: non-empty replaces the OAuth client secret. */
  gmail_client_secret?: string;
  /** Gmail API sender: forget the connected Google account. */
  disconnect_gmail?: boolean;
}

export interface TestChannelBody {
  /** Comma-separated recipients; empty = only connect / authenticate. */
  to?: string;
  /** Unsaved form values to test instead of the stored ones. */
  settings?: Record<string, any>;
  secret?: string;
  gmail_client_secret?: string;
}

export interface AlertChannelsApi {
  saveChannel: (channel: AlertChannelKey, body: SaveChannelBody) => Promise<AlertChannelState>;
  testChannel: (channel: AlertChannelKey, body: TestChannelBody) => Promise<{ ok: boolean; message: string }>;
  /**
   * Present where the Gmail API sender exists (the cloud, and a site): start "Connect Google
   * account" for the saved Client ID/secret; resolves with Google's consent URL.
   */
  gmailConnect?: (redirectUri: string) => Promise<{ auth_url: string }>;
  /**
   * The callback Google must redirect to, when it is not this page's own origin — a site connects
   * through its cloud's public callback (Google will not redirect to http://192.168.x.x).
   */
  gmailRedirectUri?: string | null;
}

/** The cloud backend's Gmail OAuth callback — what goes under "Authorized redirect URIs". */
export const GMAIL_CALLBACK_PATH = '/api/alert-actions/gmail/callback';

/**
 * Who sends a SITE's email / Message API notifications — one switch per site (its Alert
 * Actions page, or the cloud's Central Alert Actions). It replaced the per-rule `notify_via`.
 */
export type HandledBy = 'site' | 'cloud';

export const CHANNEL_TITLE: Record<AlertChannelKey, string> = {
  email: 'Email (SMTP)',
  message_api: 'Message API Server',
};

export function splitRecipients(value: unknown): string[] {
  return [...new Set(String(value || '').split(/[,;\n]/).map((v) => v.trim()).filter(Boolean))];
}

/** Error text out of whatever shape an axios failure arrived in. */
export function alertActionsErrorText(err: any, fallback: string): string {
  return err?.response?.data?.error?.message || err?.response?.data?.message || err?.message || fallback;
}

// ─── System alerts: CPU / memory / disk rules, offline, delivery log ──────────────

/**
 * One CPU / memory / disk rule — a site's own (it evaluates them itself) or the cloud's own. The
 * same shape on both nodes, so SystemRulesTable edits either.
 */
export interface SystemRule {
  alert_type: 'cpu_high' | 'memory_high' | 'disk_high';
  enabled: boolean;
  warning_threshold: number;
  critical_threshold: number;
  consecutive_checks: number;
  cooldown_minutes: number;
}

export const SYSTEM_RULE_LABEL: Record<SystemRule['alert_type'], string> = {
  cpu_high: 'CPU usage',
  memory_high: 'Memory usage',
  disk_high: 'Disk usage',
};

/** Someone "also notify users with access to this site" reaches. */
export interface SiteUser {
  id: string;
  name: string;
  email: string;
}

/**
 * One row of a delivery log — the cloud's (everything it sent) or a site's (what it sent or
 * handed to the cloud). Each app maps its own rows into this.
 */
export type DeliveryKind = 'ai' | 'system' | 'offline' | 'cloud';
export interface DeliveryLogEntry {
  id: string | number;
  at: string;
  kind: DeliveryKind;
  /** The site (cloud log only). */
  site?: string | null;
  title: string;
  detail?: string | null;
  /** Camera (AI) or service / machine (system). */
  source?: string | null;
  /** Site log only: sent by the site, or handed to the cloud. */
  via?: 'site' | 'cloud' | null;
  status: string;
  /** A queued row: tries so far, and when the next one is due (null = the cloud retries it). */
  attempts?: number;
  retry_at?: string | null;
  requested?: Record<string, string[]>;
  results: { channel: string | null; ok: boolean; sent?: number; failed?: number; recipients?: string[]; error?: string; ms?: number }[];
  /** Why it went somewhere unexpected, or nowhere: a pause, sent to the default instead, … */
  note?: string | null;
  /** How long the whole send took. */
  duration_ms?: number | null;
}

// ─── Routing policies (who gets which alert) ───────────────────────

export type PolicyKind = 'ai' | 'system';
/** What one notification covers: AI alerts, system problems, or both. */
/** + 'offline': the cloud's Site offline cards (sites in `cameras`, no `alerts`). */
export type PolicyScope = PolicyKind | 'both' | 'offline';
export type SystemAlertKind = 'service' | 'cpu' | 'memory' | 'disk' | 'camera';

/** How one person is told: a pop-up on their screen, email, a message through the Message API gateway. */
export interface PolicyChannels {
  dashboard: boolean;
  email: boolean;
  message_api: boolean;
}

/**
 * One pick on a level or in a contact group, with its own channels: a user, everyone with a role,
 * a contact group (its members say how — no channels here) or someone without an account.
 */
export interface PolicyPerson {
  kind: 'user' | 'role' | 'group' | 'contact';
  id?: string;
  name?: string;
  email?: string;
  /** A contact's number — or a Message API group chat ID (`…@g.us`). */
  phone?: string;
  /** Group entries and people on a step say how; a contact group's members do not (the step picking the group does). */
  channels?: PolicyChannels;
  /** Editor only: this contact row is a Message API group (saved, it is told by its `@g.us` phone). */
  whatsapp_group?: boolean;
}

export interface PolicyLevel {
  /** Minutes after the alert. Level 1 is always 0 (sent at once). */
  after_min: number;
  people: PolicyPerson[];
}

/** A named group of people to notify, each with their own channels — picked as one in Routing. */
export interface ContactGroup {
  id: string;
  name: string;
  members: PolicyPerson[];
  updated_at?: string;
  updated_by?: string | null;
}

/** The cloud server's own problems a Cloud (platform) card can pick. */
export const CLOUD_PROBLEMS: { value: string; label: string }[] = [
  { value: 'cpu', label: 'CPU too busy' },
  { value: 'memory', label: 'Memory almost full' },
  { value: 'disk', label: 'Disk almost full' },
  { value: 'error', label: 'A critical error' },
];

/** Watchlist hits a notification can pick — "<subject>:<category>", as the site matches them. */
export const WATCHLIST_ROUTING_CATEGORIES: { value: string; label: string }[] = [
  { value: 'person:blacklisted', label: 'Blacklisted person' },
  { value: 'person:wanted', label: 'Wanted person' },
  { value: 'person:missing', label: 'Missing person' },
  { value: 'vehicle:blacklisted', label: 'Blacklisted vehicle' },
  { value: 'vehicle:stolen', label: 'Stolen vehicle' },
];

export const CHANNEL_WORD: Record<keyof PolicyChannels, string> = { dashboard: 'pop-up', email: 'email', message_api: 'Message API' };

/**
 * A routing policy: these cameras + this alert → these people. Every enabled policy that matches
 * applies; the kind's default only when none other matched. Lives on the site (its users and
 * cameras); the cloud edits a site's over the tunnel.
 */
export interface RoutingPolicy {
  id: string;
  name: string;
  /** 'both' = AI alerts and system problems (cameras/board apply to its AI alerts). Defaults are one kind. */
  kind: PolicyScope;
  is_default: boolean;
  enabled: boolean;
  position: number;
  cameras: { mode: 'all' | 'groups' | 'cameras'; ids: string[] };
  alerts: {
    /** false = alerts from rules not picked (a watchlist-only notification). Absent = picked. */
    ai?: boolean;
    event_types?: string[];
    rule_ids?: string[];
    /** For alerts from rules AND watchlist hits. */
    min_severity?: 'warning' | 'critical' | null;
    /** Watchlist hits: null/absent = not picked, [] = every one, else these (WATCHLIST_ROUTING_CATEGORIES). */
    watchlist?: string[] | null;
    kinds?: SystemAlertKind[];
    /** Cloud (platform) cards: a site going offline (its sites in `cameras`), absent = yes. */
    offline?: boolean;
    /** Cloud (platform) cards: the cloud server's own problems — null = not picked, [] = every one (CLOUD_PROBLEMS). */
    cloud?: string[] | null;
  };
  levels: PolicyLevel[];
  /** After the last level: repeat it every `every_min`, at most `max` times (AI policies). */
  reminder?: { every_min: number; max: number } | null;
  /** AI: show these alerts on the live alert board (a delivery channel; default on). */
  board?: boolean;
  updated_at?: string;
}

export interface PolicyDirectory {
  users: { id: string; name: string; email: string; phone: string | null; notify_email: boolean; notify_message: boolean; role: string; cloud: boolean }[];
  roles: { id: string; name: string }[];
  cameras: { id: string; name: string }[];
  groups: { id: string; name: string; parent_id: string | null }[];
  rules: {
    id: string; name: string | null; event_type: string; is_active: boolean;
    /** Shown on the alert board. */
    board?: boolean;
    /** An older rule still limited to these cameras (or a group): its alerts only come from there. */
    cameras?: string[] | null;
    group_id?: string | null;
  }[];
  contact_groups?: ContactGroup[];
}

export interface PolicyPreview {
  policies: { id: string; name: string; is_default: boolean }[];
  levels: {
    level: number;
    wanted: { email?: string[]; message_api?: string[] };
    people: { type: 'user' | 'contact'; id?: string; name: string; email: string | null; phone: string | null; dashboard?: boolean }[];
    left_out: { name: string; reason: string }[];
  }[];
  /** The matched notifications reach nobody at once — the default is told instead. */
  fallback?: { policies: string[]; reason: string; note: string } | null;
}

/** One change to routing (a notification or a pause): who, when, what. */
export interface PolicyChange {
  id: number | string;
  entity: 'policy' | 'silence' | 'group';
  entity_id: string;
  kind: PolicyScope | 'all' | null;
  name: string | null;
  action: 'created' | 'updated' | 'deleted' | 'reordered' | 'cancelled';
  /** For 'updated': what changed, in words ("who is told", "which alerts"). */
  changes: string[];
  changed_by: string | null;
  at: string;
}

/** What the Routing screen needs — the site's own API, or the cloud's proxy for one site. */
export interface RoutingPoliciesApi {
  /** No kind = every notification (both kinds). */
  list: (kind?: PolicyKind) => Promise<RoutingPolicy[]>;
  directory: () => Promise<PolicyDirectory>;
  /** Create (id null) or update. */
  save: (id: string | null, body: Partial<RoutingPolicy>) => Promise<RoutingPolicy>;
  remove: (id: string) => Promise<unknown>;
  preview: (kind: PolicyKind, sample: Record<string, unknown>) => Promise<PolicyPreview>;
  /** Who changed routing, newest first (absent on an older site). */
  history?: (opts?: { entity?: 'policy' | 'silence' | 'group'; limit?: number }) => Promise<PolicyChange[]>;
  /** Contact groups (absent on an older site). */
  groups?: {
    save: (id: string | null, body: Partial<ContactGroup>) => Promise<ContactGroup>;
    remove: (id: string) => Promise<unknown>;
  };
}

// ─── Pause notifications (maintenance windows) ─────────────────────

/**
 * A pause: while in force, matching alerts are still recorded (board, lists, reports) but nobody is
 * notified and nothing escalates. Every non-empty list narrows it; all empty = every alert of `kind`.
 */
export interface AlertSilence {
  id: string;
  reason: string;
  kind: PolicyKind | 'all';
  camera_ids: string[];
  group_ids: string[];
  rule_ids: string[];
  system_kinds: SystemAlertKind[];
  starts_at: string;
  ends_at: string;
  cancelled_at: string | null;
  created_by: string | null;
  cancelled_by: string | null;
  state: 'active' | 'upcoming' | 'ended' | 'cancelled';
}

export interface SilencesApi {
  list: () => Promise<AlertSilence[]>;
  save: (id: string | null, body: Partial<AlertSilence>) => Promise<AlertSilence>;
  cancel: (id: string) => Promise<AlertSilence>;
  /** Pickers: cameras, groups, rules (the routing directory). */
  directory: () => Promise<PolicyDirectory>;
}

// ─── Delivery report ───────────────────────────────────────────────

export interface DeliveryStats {
  range: { days: number; tz: string };
  totals: {
    total: number; sent: number; partial: number; failed: number; queued: number; skipped: number; silenced: number;
    sending: number; rerouted: number; via_site: number; via_cloud: number; ms_avg: number | null; ms_p90: number | null;
  };
  channels: { channel: string; sent: number; failed: number; success_rate: number | null; ms_avg: number | null; ms_p90: number | null }[];
  by_day: { day: string; total: number; sent: number; failed: number; silenced: number }[];
  noisiest: { kind: string; title: string; total: number; failed: number; escalations: number; last_at: string }[];
  failures: { error: string; channel: string | null; count: number }[];
}

export const SYSTEM_ALERT_KINDS: { value: SystemAlertKind; label: string }[] = [
  { value: 'service', label: 'A service stopped' },
  { value: 'cpu', label: 'CPU too busy' },
  { value: 'memory', label: 'Memory almost full' },
  { value: 'disk', label: 'Disk almost full' },
  { value: 'camera', label: 'A camera stopped streaming' },
];

/** Why an alert or system incident happened — picked when resolving (the post-incident report). */
export const RESOLUTION_CAUSES: { value: string; label: string }[] = [
  { value: 'power', label: 'Power cut' },
  { value: 'network', label: 'Network problem' },
  { value: 'hardware', label: 'Hardware fault' },
  { value: 'software', label: 'Software problem' },
  { value: 'camera', label: 'Camera problem' },
  { value: 'human', label: 'Someone did it' },
  { value: 'external', label: 'Outside cause' },
  { value: 'false_alarm', label: 'False alarm' },
  { value: 'unknown', label: 'Not known' },
];
export const causeLabel = (v: string | null | undefined) => RESOLUTION_CAUSES.find((c) => c.value === v)?.label || v || '';

export function emptyPolicyLevel(afterMin = 0): PolicyLevel {
  return { after_min: afterMin, people: [] };
}

// ─── Escalations tab: metrics + system alerts ──────────────────────

export interface ResponseMetrics {
  total: number;
  responded: number;
  resolved: number;
  open: number;
  escalated: number;
  ended: number;
  /** Seconds. */
  tta: { avg: number | null; p90: number | null };
  ttr: { avg: number | null; p90: number | null };
  by_day: { day: string; total: number; responded: number; escalated: number; ended: number; tta_avg: number | null }[];
  by_type: { event_type: string; total: number; escalated: number; ended: number; tta_avg: number | null }[];
  levels: { level: number; total: number; responded: number }[];
  responders: { name: string; responses: number; avg_secs: number | null; after_escalation: number }[];
  /** Causes picked when resolving (newer command-centers). */
  by_cause?: { cause: string; total: number }[];
  /** Repeats inside a rule's cooldown, counted on the alert instead of dropped. */
  repeats?: { events: number; alerts: number };
}

export interface EscalationMetrics {
  range: { days: number; from: string; to: string; tz: string };
  /** AI alerts — null when the command-center did not answer (see response_error). */
  response: ResponseMetrics | null;
  response_error?: string;
  policies: {
    policy_id: string; kind: PolicyKind; name: string; started: number; acknowledged: number;
    acknowledged_after_escalation: number; ended: number; running: number; avg_level: number | null; avg_ack_secs: number | null;
  }[];
  open: { alert_id: string; kind: PolicyKind; title: string; source: string | null; policy: string; level: number; reminders: number; next_at: string | null; started_at: string }[];
  system: {
    total: number; acknowledged: number; resolved: number; live: number; escalated: number; ended: number;
    tta: { avg: number | null; p90: number | null };
    by_kind: { kind: string; total: number; acknowledged: number; escalated: number }[];
  };
}

/** One of a site's system incidents (its system_alerts). */
export interface SiteSystemAlert {
  id: string;
  kind: SystemAlertKind;
  title: string | null;
  severity: string | null;
  service: string | null;
  machine: string | null;
  message: string | null;
  status: 'open' | 'acknowledged' | 'resolved';
  occurrences: number;
  escalation_level: number | null;
  escalation_state: 'active' | 'ended' | null;
  first_at: string;
  last_at: string;
  acknowledged_at: string | null;
  acknowledged_by: string | null;
  resolved_at: string | null;
  resolved_by: string | null;
  /** Recorded but not sent: a pause, or the service that serves the cameras was already down. */
  suppressed?: 'silenced' | 'parent' | null;
  cause?: string | null;
  resolution_note?: string | null;
  /** 'auto' — it recovered by itself (service running again, camera streaming again). */
  resolved_how?: 'person' | 'auto' | null;
}

/** What the Escalations tab needs — the site's own API, or the cloud's proxy for one site. */
export interface EscalationsApi {
  metrics: (days: number) => Promise<EscalationMetrics>;
  systemAlerts: (status: 'live' | 'all') => Promise<SiteSystemAlert[]>;
  setSystemAlertStatus: (id: string, action: 'acknowledge' | 'resolve', report?: { cause?: string; note?: string }) => Promise<SiteSystemAlert>;
}

/** 95 → "1 min 35 s", 7300 → "2 h 2 min". */
export function durationText(secs: number | null | undefined): string {
  if (secs === null || secs === undefined || !Number.isFinite(secs)) return '—';
  const s = Math.max(0, Math.round(secs));
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  if (m < 60) return s % 60 && m < 10 ? `${m} min ${s % 60} s` : `${m} min`;
  const h = Math.floor(m / 60);
  return m % 60 ? `${h} h ${m % 60} min` : `${h} h`;
}
